import type { AdminRole, AuthenticatedAdmin } from "@utopia-well-done-ltd/contracts";
import { type ReactNode, useEffect, useState } from "react";
import {
  API_ERROR_EVENT,
  type ApiError,
  api,
  REAUTH_REQUIRED_EVENT,
  SESSION_EXPIRED_EVENT,
} from "./api/client.js";
import oasisLogoDark from "./assets/brand/oasis-logo-dark.png";
import oasisLogoLight from "./assets/brand/oasis-logo-light.png";
import approvalsIcon from "./assets/generated/icon-approvals-v3.png";
import auditIcon from "./assets/generated/icon-audit-v3.png";
import blackjackIcon from "./assets/generated/icon-blackjack-v1.svg";
import profitIcon from "./assets/generated/icon-casino-profit-v3.png";
import dashboardIcon from "./assets/generated/icon-dashboard-v1.png";
import playersIcon from "./assets/generated/icon-players-v3.png";
import pokerIcon from "./assets/generated/icon-poker-v1.png";
import roundsIcon from "./assets/generated/icon-rounds-v3.png";
import royalSpinIcon from "./assets/generated/icon-royal-spin-v3.png";
import transactionsIcon from "./assets/generated/icon-transactions-v3.png";
import { can } from "./capabilities.js";
import { CommandPalette } from "./components/CommandPalette.js";
import { CopyButton } from "./components/ExplorerTools.js";
import { LanguageToggle } from "./components/LanguageToggle.js";
import { OperatorGuide } from "./components/OperatorGuide.js";
import { Reauthenticate } from "./components/Reauthenticate.js";
import { RoundDetailModal } from "./components/RoundDetailModal.js";
import { ThemeToggle } from "./components/ThemeToggle.js";
import { setFormatLanguage } from "./format.js";
import { type Language, LanguageProvider, useFrenchUi } from "./i18n.js";
import { Actions } from "./screens/Actions.js";
import { Audit } from "./screens/Audit.js";
import { BlackjackOperations } from "./screens/BlackjackOperations.js";
import { Cases } from "./screens/Cases.js";
import { ChatModeration } from "./screens/ChatModeration.js";
import { Dashboard } from "./screens/Dashboard.js";
import { Environments } from "./screens/Environments.js";
import { Feedback, FeedbackBadge } from "./screens/Feedback.js";
import { Login } from "./screens/Login.js";
import { Operators } from "./screens/Operators.js";
import { Players } from "./screens/Players.js";
import { PokerOperations } from "./screens/PokerOperations.js";
import { Profit } from "./screens/Profit.js";
import { Rooms } from "./screens/Rooms.js";
import { RouletteOperations } from "./screens/RouletteOperations.js";
import { Rounds } from "./screens/Rounds.js";
import { RoyalSpin } from "./screens/RoyalSpin.js";
import { Transactions } from "./screens/Transactions.js";
import { Waitlist } from "./screens/Waitlist.js";

type Screen =
  | "actions"
  | "audit"
  | "blackjack"
  | "cases"
  | "feedback"
  | "chat"
  | "dashboard"
  | "environments"
  | "operators"
  | "players"
  | "poker"
  | "profit"
  | "royal-spin"
  | "roulette"
  | "rooms"
  | "rounds"
  | "transactions"
  | "waitlist";
type Theme = "dark" | "light";

const SCREEN_COPY: Record<Language, Record<Screen, { description: string; title: string }>> = {
  en: {
    operators: {
      title: "Operators & moderator access",
      description: "Manage backoffice role requests and player moderator grants.",
    },
    chat: {
      title: "Chat moderation",
      description: "Review messages and manage chat restrictions.",
    },
    waitlist: {
      title: "Launch waitlist",
      description: "Email signups collected from the public pre-launch page.",
    },
    feedback: {
      title: "Player feedback",
      description: "Suggestions, technical problems and complaints submitted in the casino.",
    },
    actions: {
      description: "Review and decide money movements waiting for a second operator.",
      title: "Approval queue",
    },
    audit: {
      description: "Trace operator activity through the append-only audit chain.",
      title: "Audit log",
    },
    blackjack: {
      description: "Monitor Blackjack authority health, live rounds, seats and deadlines.",
      title: "Blackjack Operations",
    },
    cases: {
      description: "Work support, KYC and responsible-gaming cases across every player.",
      title: "Case queue",
    },
    dashboard: {
      description: "Monitor today's gaming activity, operational queues and integrity health.",
      title: "Operations Dashboard",
    },
    environments: {
      description:
        "Inspect runtime ownership and move table environments through safe lifecycle boundaries.",
      title: "Table Environments",
    },
    players: {
      description: "Find players, inspect their account history and take support actions.",
      title: "Players",
    },
    poker: {
      description: "Monitor Poker authority health, table state, seats and leases.",
      title: "Poker Operations",
    },
    profit: {
      description: "Track settled wagering, player returns and gross gaming revenue.",
      title: "Casino Profit",
    },
    "royal-spin": {
      description: "Create, approve and safely roll out Royal Spin campaigns.",
      title: "Royal Spin",
    },
    roulette: {
      description: "Monitor Roulette authority and safely schedule approved limit profiles.",
      title: "Roulette Operations",
    },
    rooms: {
      description: "Monitor active runtime rooms, capacity and the users connected to each room.",
      title: "Rooms",
    },
    rounds: {
      description: "Search game rounds, money, actions and integrity evidence.",
      title: "Round Explorer",
    },
    transactions: {
      description: "Search player money movements, references and correction chains.",
      title: "Transaction Explorer",
    },
  },
  fr: {
    operators: {
      title: "Opérateurs et modérateurs",
      description: "Gérez les rôles du backoffice et les accès de modération des joueurs.",
    },
    chat: {
      title: "Modération du chat",
      description: "Examinez les messages et gérez les restrictions du chat.",
    },
    waitlist: {
      title: "Liste d’attente",
      description: "Inscriptions e-mail collectées sur la page publique de pré-lancement.",
    },
    feedback: {
      title: "Retours des joueurs",
      description: "Suggestions, problèmes techniques et réclamations envoyés depuis le casino.",
    },
    actions: {
      description:
        "Examinez et décidez des mouvements financiers en attente d'un second opérateur.",
      title: "File d’approbation",
    },
    audit: {
      description: "Retracez l’activité des opérateurs dans la chaîne d’audit immuable.",
      title: "Journal d’audit",
    },
    blackjack: {
      description: "Surveillez l’autorité Blackjack, les manches, les sièges et les échéances.",
      title: "Opérations Blackjack",
    },
    cases: {
      description: "Traitez les dossiers support, KYC et jeu responsable de tous les joueurs.",
      title: "File des dossiers",
    },
    dashboard: {
      description: "Surveillez l’activité de jeu, les files opérationnelles et l’intégrité.",
      title: "Tableau de bord opérationnel",
    },
    environments: {
      description:
        "Inspectez les propriétaires d’exécution et gérez le cycle de vie sécurisé des environnements.",
      title: "Environnements de tables",
    },
    players: {
      description: "Recherchez les joueurs et consultez l’historique de leur compte.",
      title: "Joueurs",
    },
    poker: {
      description: "Surveillez l’autorité Poker, la table, les sièges et les baux.",
      title: "Opérations Poker",
    },
    profit: {
      description: "Suivez les mises réglées, les retours joueurs et le produit brut des jeux.",
      title: "Produit du casino",
    },
    "royal-spin": {
      description: "Créez, approuvez et déployez les campagnes Royal Spin en toute sécurité.",
      title: "Royal Spin",
    },
    roulette: {
      description: "Surveillez l’autorité Roulette et planifiez les profils de limites approuvés.",
      title: "Opérations Roulette",
    },
    rooms: {
      description: "Surveillez les salles actives, leur capacité et leurs utilisateurs connectés.",
      title: "Salles",
    },
    rounds: {
      description: "Recherchez les manches, les montants, les actions et les preuves d’intégrité.",
      title: "Explorateur de manches",
    },
    transactions: {
      description: "Recherchez les mouvements financiers, références et chaînes de correction.",
      title: "Explorateur de transactions",
    },
  },
};

const SHELL_COPY = {
  en: {
    approvals: "Approvals",
    audit: "Audit",
    blackjack: "Blackjack",
    cases: "Cases",
    dashboard: "Dashboard",
    close: "Close",
    economy: "Economy & promotions",
    environments: "Environments",
    language: "Language",
    menu: "Menu",
    operations: "Operations",
    players: "Players",
    poker: "Poker",
    roulette: "Roulette",
    playerOperations: "Player operations",
    profit: "Casino Profit",
    rounds: "Rounds",
    rooms: "Rooms",
    signOut: "Sign out",
    transactions: "Transactions",
  },
  fr: {
    approvals: "Approbations",
    audit: "Audit",
    blackjack: "Blackjack",
    cases: "Dossiers",
    dashboard: "Tableau de bord",
    close: "Fermer",
    economy: "Économie et promotions",
    environments: "Environnements",
    language: "Langue",
    menu: "Menu",
    operations: "Opérations",
    players: "Joueurs",
    poker: "Poker",
    roulette: "Roulette",
    playerOperations: "Opérations joueurs",
    profit: "Produit du casino",
    rounds: "Manches",
    rooms: "Salles",
    signOut: "Se déconnecter",
    transactions: "Transactions",
  },
} as const;

const initialScreen = (): Screen => {
  const value = new URLSearchParams(window.location.search).get("screen");
  return value && value in SCREEN_COPY.en ? (value as Screen) : "dashboard";
};

function canViewScreen(role: AdminRole, screen: Screen) {
  switch (screen) {
    case "chat":
      return can(role, "players.status.write");
    case "operators":
      return can(role, "admins.initiate") || can(role, "admins.approve");
    case "actions":
      return can(role, "wallet.reversal.initiate") || can(role, "wallet.reversal.approve");
    case "audit":
      return can(role, "audit.read");
    case "players":
      return can(role, "players.read");
    case "cases":
    case "feedback":
    case "waitlist":
      return can(role, "cases.read");
    case "blackjack":
    case "environments":
    case "poker":
    case "roulette":
    case "rooms":
      return can(role, "games.read");
    case "profit":
      return can(role, "reports.financial.read");
    case "royal-spin":
      return can(role, "royal_spin.campaign.read");
    case "rounds":
      return can(role, "games.read");
    case "transactions":
      return can(role, "ledger.read");
    default:
      return true;
  }
}

function NavButton({
  active,
  children,
  icon,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  icon: string;
  onClick: () => void;
}) {
  return (
    <button
      aria-current={active ? "page" : undefined}
      className="side-link"
      onClick={onClick}
      type="button"
    >
      <img alt="" aria-hidden="true" className="side-icon" src={icon} />
      {children}
    </button>
  );
}

export function App() {
  const [admin, setAdmin] = useState<AuthenticatedAdmin | null>(null);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const [checked, setChecked] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [operationRoundId, setOperationRoundId] = useState<string | null>(null);
  const [reauthRequired, setReauthRequired] = useState(false);
  const [screen, setScreen] = useState<Screen>(initialScreen);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [language, setLanguage] = useState<Language>(() =>
    localStorage.getItem("utopia-backoffice-language") === "fr" ? "fr" : "en",
  );
  const [theme, setTheme] = useState<Theme>(() =>
    localStorage.getItem("utopia-backoffice-theme") === "dark" ? "dark" : "light",
  );
  setFormatLanguage(language);
  useFrenchUi(language);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("utopia-backoffice-theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem("utopia-backoffice-language", language);
  }, [language]);

  useEffect(() => {
    const channel =
      typeof window.BroadcastChannel === "function"
        ? new window.BroadcastChannel("utopia-backoffice-session")
        : null;
    const clearSession = () => {
      setAdmin(null);
      setApiError(null);
      setMobileNavOpen(false);
      setReauthRequired(false);
    };
    const expireSession = () => {
      clearSession();
      channel?.postMessage("expired");
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, expireSession);
    channel?.addEventListener("message", clearSession);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, expireSession);
      channel?.removeEventListener("message", clearSession);
      channel?.close();
    };
  }, []);

  useEffect(() => {
    const requireReauth = () => setReauthRequired(true);
    window.addEventListener(REAUTH_REQUIRED_EVENT, requireReauth);
    return () => window.removeEventListener(REAUTH_REQUIRED_EVENT, requireReauth);
  }, []);

  useEffect(() => {
    const showApiError = (event: Event) => setApiError((event as CustomEvent<ApiError>).detail);
    window.addEventListener(API_ERROR_EVENT, showApiError);
    return () => window.removeEventListener(API_ERROR_EVENT, showApiError);
  }, []);

  useEffect(() => {
    if (!admin) return;
    let controller: AbortController | null = null;
    const revalidate = () => {
      controller?.abort();
      controller = new AbortController();
      api
        .me(controller.signal)
        .then(({ admin: current }) => setAdmin(current))
        .catch(() => undefined);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") revalidate();
    };
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      controller?.abort();
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [admin]);

  useEffect(() => {
    const restoreScreen = () => {
      setMobileNavOpen(false);
      setScreen(initialScreen());
    };
    window.addEventListener("popstate", restoreScreen);
    return () => window.removeEventListener("popstate", restoreScreen);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    const closeMenu = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileNavOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeMenu);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeMenu);
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!screen) return;
    const active = document.querySelector<HTMLElement>('.side-link[aria-current="page"]');
    const nav = active?.closest<HTMLElement>(".side-nav");
    if (!active || !nav) return;
    nav.scrollTo?.({
      behavior: "smooth",
      left: active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2,
    });
  }, [screen]);

  useEffect(() => {
    const controller = new AbortController();

    api
      .me(controller.signal)
      .then(({ admin: current }) => setAdmin(current))
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setChecked(true);
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!admin || canViewScreen(admin.role, screen)) return;
    window.history.replaceState(null, "", `${window.location.pathname}?screen=dashboard`);
    setScreen("dashboard");
  }, [admin, screen]);

  if (!checked) return <p className="empty">Loading…</p>;
  if (!admin)
    return (
      <LanguageProvider language={language}>
        <Login
          key={language}
          language={language}
          onLanguageChange={setLanguage}
          onSignedIn={setAdmin}
          onThemeChange={setTheme}
          theme={theme}
        />
      </LanguageProvider>
    );

  const copy = SCREEN_COPY[language][screen];
  const shell = SHELL_COPY[language];
  const destinations = (
    [
      ["dashboard", shell.dashboard],
      ["players", shell.players],
      ["cases", shell.cases],
      ["feedback", SCREEN_COPY[language].feedback.title],
      ["chat", SCREEN_COPY[language].chat.title],
      ["operators", SCREEN_COPY[language].operators.title],
      ["waitlist", SCREEN_COPY[language].waitlist.title],
      ["transactions", shell.transactions],
      ["actions", shell.approvals],
      ["profit", shell.profit],
      ["royal-spin", "Royal Spin"],
      ["rounds", shell.rounds],
      ["environments", shell.environments],
      ["rooms", shell.rooms],
      ["blackjack", shell.blackjack],
      ["poker", shell.poker],
      ["roulette", shell.roulette],
      ["audit", shell.audit],
    ] as const
  )
    .filter(([destination]) => canViewScreen(admin.role, destination))
    .map(([destination, label]) => ({ label, search: `?screen=${destination}` }));
  const navigate = (next: Screen) => {
    setMobileNavOpen(false);
    setApiError(null);
    window.history.pushState(null, "", `${window.location.pathname}?screen=${next}`);
    setScreen(next);
  };
  const openRound = (roundId: string) => {
    setMobileNavOpen(false);
    setApiError(null);
    setOperationRoundId(roundId);
  };
  const signOut = () => {
    setSignOutError(null);
    api
      .signOut()
      .then(() => {
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      })
      .catch(() => setSignOutError("Could not sign out. Try again."));
  };

  return (
    <main className="app-shell" key={language}>
      {reauthRequired ? <Reauthenticate onClose={() => setReauthRequired(false)} /> : null}
      {mobileNavOpen ? (
        <button
          aria-label={shell.close}
          className="mobile-nav-backdrop"
          onClick={() => setMobileNavOpen(false)}
          type="button"
        />
      ) : null}
      <aside className={`sidebar${mobileNavOpen ? " mobile-menu-open" : ""}`}>
        <div className="side-brand">
          <img alt="Oasis" className="side-brand-logo brand-for-light" src={oasisLogoDark} />
          <img alt="Oasis" className="side-brand-logo brand-for-dark" src={oasisLogoLight} />
        </div>

        <nav aria-label="Oasis Backoffice" className="side-nav" id="mobile-navigation">
          <NavButton
            active={screen === "dashboard"}
            icon={dashboardIcon}
            onClick={() => navigate("dashboard")}
          >
            {shell.dashboard}
          </NavButton>
          <span className="side-section">{shell.playerOperations}</span>
          {can(admin.role, "players.status.write") ? (
            <NavButton
              active={screen === "chat"}
              icon={playersIcon}
              onClick={() => navigate("chat")}
            >
              {SCREEN_COPY[language].chat.title}
            </NavButton>
          ) : null}
          {canViewScreen(admin.role, "operators") ? (
            <NavButton
              active={screen === "operators"}
              icon={playersIcon}
              onClick={() => navigate("operators")}
            >
              {SCREEN_COPY[language].operators.title}
            </NavButton>
          ) : null}
          <NavButton
            active={screen === "players"}
            icon={playersIcon}
            onClick={() => navigate("players")}
          >
            {shell.players}
          </NavButton>
          {can(admin.role, "cases.read") ? (
            <NavButton
              active={screen === "feedback"}
              icon={playersIcon}
              onClick={() => navigate("feedback")}
            >
              {SCREEN_COPY[language].feedback.title}
              <FeedbackBadge key={admin.id} />
            </NavButton>
          ) : null}
          {can(admin.role, "cases.read") ? (
            <NavButton
              active={screen === "waitlist"}
              icon={playersIcon}
              onClick={() => navigate("waitlist")}
            >
              {language === "fr" ? "Liste d’attente" : "Waitlist"}
            </NavButton>
          ) : null}
          {can(admin.role, "cases.read") ? (
            <NavButton
              active={screen === "cases"}
              icon={approvalsIcon}
              onClick={() => navigate("cases")}
            >
              {shell.cases}
            </NavButton>
          ) : null}
          {can(admin.role, "ledger.read") ? (
            <NavButton
              active={screen === "transactions"}
              icon={transactionsIcon}
              onClick={() => navigate("transactions")}
            >
              {shell.transactions}
            </NavButton>
          ) : null}
          {can(admin.role, "wallet.reversal.initiate") ||
          can(admin.role, "wallet.reversal.approve") ? (
            <NavButton
              active={screen === "actions"}
              icon={approvalsIcon}
              onClick={() => navigate("actions")}
            >
              {shell.approvals}
            </NavButton>
          ) : null}

          <span className="side-section">{shell.economy}</span>
          {can(admin.role, "reports.financial.read") ? (
            <NavButton
              active={screen === "profit"}
              icon={profitIcon}
              onClick={() => navigate("profit")}
            >
              {shell.profit}
            </NavButton>
          ) : null}
          {can(admin.role, "royal_spin.campaign.read") ? (
            <NavButton
              active={screen === "royal-spin"}
              icon={royalSpinIcon}
              onClick={() => navigate("royal-spin")}
            >
              Royal Spin
            </NavButton>
          ) : null}

          <span className="side-section">{shell.operations}</span>
          {can(admin.role, "games.read") ? (
            <NavButton
              active={screen === "environments"}
              icon={roundsIcon}
              onClick={() => navigate("environments")}
            >
              {shell.environments}
            </NavButton>
          ) : null}
          {can(admin.role, "games.read") ? (
            <NavButton
              active={screen === "rooms"}
              icon={roundsIcon}
              onClick={() => navigate("rooms")}
            >
              {shell.rooms}
            </NavButton>
          ) : null}
          {can(admin.role, "games.read") ? (
            <NavButton
              active={screen === "blackjack"}
              icon={blackjackIcon}
              onClick={() => navigate("blackjack")}
            >
              {shell.blackjack}
            </NavButton>
          ) : null}
          {can(admin.role, "games.read") ? (
            <NavButton
              active={screen === "poker"}
              icon={pokerIcon}
              onClick={() => navigate("poker")}
            >
              {shell.poker}
            </NavButton>
          ) : null}
          {can(admin.role, "games.read") ? (
            <NavButton
              active={screen === "roulette"}
              icon={roundsIcon}
              onClick={() => navigate("roulette")}
            >
              {shell.roulette}
            </NavButton>
          ) : null}
          {can(admin.role, "games.read") ? (
            <NavButton
              active={screen === "rounds"}
              icon={roundsIcon}
              onClick={() => navigate("rounds")}
            >
              {shell.rounds}
            </NavButton>
          ) : null}
          {can(admin.role, "audit.read") ? (
            <NavButton
              active={screen === "audit"}
              icon={auditIcon}
              onClick={() => navigate("audit")}
            >
              {shell.audit}
            </NavButton>
          ) : null}
          <button
            aria-controls="mobile-navigation"
            aria-current={
              !mobileNavOpen && ["profit", "royal-spin", "rounds", "audit"].includes(screen)
                ? "page"
                : undefined
            }
            aria-expanded={mobileNavOpen}
            aria-label={mobileNavOpen ? shell.close : shell.menu}
            className="side-link mobile-menu-toggle"
            onClick={() => setMobileNavOpen((open) => !open)}
            type="button"
          >
            <svg aria-hidden="true" className="side-icon" fill="none" viewBox="0 0 24 24">
              <path
                d={mobileNavOpen ? "M6 6l12 12M18 6 6 18" : "M5 7h14M5 12h14M5 17h14"}
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.8"
              />
            </svg>
            {mobileNavOpen ? shell.close : shell.menu}
          </button>
        </nav>

        <div className="side-preferences">
          <ThemeToggle
            dark={theme === "dark"}
            onToggle={() => setTheme((current) => (current === "dark" ? "light" : "dark"))}
          />
          <LanguageToggle language={language} label={shell.language} onChange={setLanguage} />
        </div>

        <div className="side-account">
          <span className="side-avatar">{admin.email.charAt(0).toUpperCase()}</span>
          <span className="side-account-copy">
            <span>{admin.email}</span>
            <span>{admin.role.replace("_", " ")}</span>
          </span>
          <button
            aria-label={shell.signOut}
            className="side-signout"
            onClick={signOut}
            type="button"
          >
            <svg aria-hidden="true" fill="none" viewBox="0 0 20 20">
              <path
                d="M8 4H5.5A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8M12.5 6.5 16 10m0 0-3.5 3.5M16 10H8"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.6"
              />
            </svg>
          </button>
        </div>
      </aside>

      <section className="workspace">
        <header className="workspace-topbar">
          <span aria-hidden="true" className="mobile-topbar-brand">
            <img alt="" className="brand-for-light" src={oasisLogoDark} />
            <img alt="" className="brand-for-dark" src={oasisLogoLight} />
          </span>
          <CommandPalette
            adminId={admin.id}
            destinations={destinations}
            language={language}
            onNavigate={(search) => {
              window.location.href = `${window.location.pathname}${search}`;
            }}
          />
          <span className="spacer" />
          <OperatorGuide language={language} />
          <span className="topbar-preferences">
            <ThemeToggle
              dark={theme === "dark"}
              onToggle={() => setTheme((current) => (current === "dark" ? "light" : "dark"))}
            />
            <LanguageToggle language={language} label={shell.language} onChange={setLanguage} />
          </span>
          <button
            aria-label={shell.signOut}
            className="btn btn-quiet btn-sm mobile-signout"
            onClick={signOut}
            type="button"
          >
            <svg aria-hidden="true" fill="none" viewBox="0 0 20 20">
              <path
                d="M8 4H5.5A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8M12.5 6.5 16 10m0 0-3.5 3.5M16 10H8"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.6"
              />
            </svg>
            <span>{shell.signOut}</span>
          </button>
        </header>

        <div className="page-head">
          <div>
            <h1>{copy.title}</h1>
            <p>{copy.description}</p>
          </div>
        </div>

        {signOutError ? (
          <p className="banner shell-banner" role="alert">
            {signOutError}
          </p>
        ) : null}

        {apiError?.correlationId ? (
          <div className="banner retry-banner shell-banner" role="alert">
            <span>
              {apiError.message} Correlation ID: <code>{apiError.correlationId}</code>
            </span>
            <CopyButton label="Copy correlation ID" value={apiError.correlationId} />
          </div>
        ) : null}

        {screen === "players" ? (
          <LanguageProvider language={language}>
            <Players admin={admin} />
          </LanguageProvider>
        ) : null}
        {screen === "dashboard" ? <Dashboard admin={admin} navigate={navigate} /> : null}
        {screen === "cases" ? <Cases admin={admin} /> : null}
        {screen === "feedback" ? <Feedback admin={admin} /> : null}
        {screen === "chat" ? (
          <LanguageProvider language={language}>
            <ChatModeration admin={admin} />
          </LanguageProvider>
        ) : null}
        {screen === "operators" ? <Operators admin={admin} /> : null}
        {screen === "waitlist" ? <Waitlist /> : null}
        {screen === "profit" ? <Profit /> : null}
        {screen === "environments" ? <Environments admin={admin} /> : null}
        {screen === "rooms" ? <Rooms /> : null}
        {screen === "blackjack" ? (
          <BlackjackOperations admin={admin} onOpenRound={openRound} />
        ) : null}
        {screen === "poker" ? <PokerOperations admin={admin} onOpenHand={openRound} /> : null}
        {screen === "roulette" ? <RouletteOperations admin={admin} /> : null}
        {screen === "actions" ? <Actions admin={admin} /> : null}
        {screen === "audit" ? <Audit /> : null}
        {screen === "royal-spin" ? <RoyalSpin admin={admin} /> : null}
        {screen === "rounds" ? <Rounds admin={admin} /> : null}
        {screen === "transactions" ? <Transactions admin={admin} /> : null}
        {operationRoundId ? (
          <RoundDetailModal
            admin={admin}
            onClose={() => setOperationRoundId(null)}
            roundId={operationRoundId}
          />
        ) : null}
      </section>
    </main>
  );
}
